import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonChip,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonSegment,
  IonSegmentButton,
  IonSpinner,
  IonTitle,
  IonToast,
  IonToolbar,
} from '@ionic/react';
import { printOutline, chevronDown, chevronForward } from 'ionicons/icons';
import type { Recipe } from '@trackeat/shared';
import {
  summarizeByDestination,
  resolveCollettivaDish,
  collettivaDietLabel,
  type DestinationSummary,
  type SummarizeInput,
  type ResolverRecipe,
} from '@trackeat/shared';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { useMasters } from '../../lib/data/queries.js';
import { useZoneBusinesses } from '../../lib/auth/useZoneBusinesses.js';
import { getSupabase } from '../../lib/supabase.js';
import { openExternalFromAsync } from '../../lib/openExternalUrl.js';

// T-465 — "Riassunto per destinazione" (comanda produzione, lato SMISTAMENTO).
// L'analogo della comanda chef (ProductionTodoPage) ma per chi smista i piatti
// alle destinazioni: replica a schermo la stampa "Lista per destinazioni"
// (edge export-piatti-zona) — piatti per destinazione/servizio + dettaglio per
// reparto in cascata. Calcolato offline dal mirror via la funzione pura
// summarizeByDestination. Tile flaggabile a parte in "Cosa vede il personale".

type CatRow = { label: string; category: string; sort_order: number };
type RecipeRow = Pick<Recipe, 'id' | 'name'> & {
  menu_label?: string | null;
  variant_of_recipe_id?: string | null;
  diet_code?: string | null;
};
type PlanItemRow = { output_name: string; category: string | null; recipe_id: string | null; diet_code: string | null };
type SRRow = { destination_id: string; lines: SummarizeInput['requests'][number]['lines']; status: string };
type DestRow = { id: string; name: string; city: string | null };

const MEAL_LABEL: Record<string, string> = { pranzo: 'Pranzo', cena: 'Cena', colazione: 'Colazione' };
const CAT_COLOR: Record<string, { bg: string; fg: string }> = {
  PRIMI: { bg: '#dbeafe', fg: '#1d4ed8' },
  SECONDI: { bg: '#fee2e2', fg: '#b91c1c' },
  CONTORNI: { bg: '#dcfce7', fg: '#15803d' },
  FRUTTA: { bg: '#fef9c3', fg: '#a16207' },
};
const todayIso = (): string => new Date().toISOString().slice(0, 10);
const norm = (s: string): string => s.trim().toLowerCase();

export default function ProductionDestinationsPage(): JSX.Element {
  const { profile } = useAuth();
  const sb = useMemo(() => getSupabase(), []);
  const { zoneIds } = useZoneBusinesses();
  const { data: recipes = [] } = useMasters<RecipeRow>('recipes');
  const [day, setDay] = useState<string>(todayIso());
  const [meal, setMeal] = useState<string>('pranzo');
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<DestinationSummary[]>([]);
  const [activeDest, setActiveDest] = useState<string>('__all__');
  const [openWards, setOpenWards] = useState<Record<string, boolean>>({});
  const [toast, setToast] = useState<string | null>(null);
  const [printing, setPrinting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    let srQ = sb
      .from('supply_requests')
      .select('destination_id,lines,status')
      .eq('requested_for_date', day)
      .in('status', ['submitted', 'accepted', 'in_production', 'fulfilled'])
      .is('archived_at', null);
    let dstQ = sb.from('destinations').select('id,name,city').is('archived_at', null).order('name');
    const catQ = sb.from('order_catalog_rows').select('label,category,sort_order');
    const planQ = sb
      .from('production_plan_items')
      .select('output_name,category,recipe_id,diet_code')
      .eq('service_date', day)
      .is('archived_at', null);
    if (zoneIds) {
      srQ = srQ.in('business_id', zoneIds);
      dstQ = dstQ.in('business_id', zoneIds);
    }
    const [sr, dst, cat, plan] = await Promise.all([srQ, dstQ, catQ, planQ]);

    const catalog = (cat.data as CatRow[] | null) ?? [];
    const planItems = (plan.data as PlanItemRow[] | null) ?? [];

    // Rotazione ordinari del giorno dal piano salvato (item con dieta nulla).
    const rotationByCategory: Record<string, string | undefined> = {};
    for (const p of planItems) {
      if (p.diet_code == null && p.category && !rotationByCategory[p.category]) rotationByCategory[p.category] = p.output_name;
    }
    const resolverRecipes: ResolverRecipe[] = recipes.map((r) => ({
      id: r.id,
      name: r.name,
      variant_of_recipe_id: r.variant_of_recipe_id ?? null,
      diet_code: r.diet_code ?? null,
    }));
    const ctx = { recipes: resolverRecipes, rotationByCategory, dietLabel: collettivaDietLabel };

    // Mappa label→nome ricetta assegnato (come il dish_recipes passato all'export).
    const dishNameByLabel: Record<string, string> = {};
    for (const c of catalog) {
      const resolved = resolveCollettivaDish(c.label, ctx);
      if (resolved && !resolved.variant_pending) dishNameByLabel[norm(c.label)] = resolved.recipe_name;
    }

    const result = summarizeByDestination({
      requests: ((sr.data as SRRow[] | null) ?? []).map((r) => ({ destination_id: r.destination_id, lines: r.lines ?? [] })),
      destinations: ((dst.data as DestRow[] | null) ?? []).map((d) => ({ id: d.id, name: d.name, city: d.city })),
      catalog,
      dishNameByLabel,
      mealFilter: meal,
    });
    setSummary(result);
    setActiveDest((cur) => (cur !== '__all__' && !result.some((d) => d.destId === cur) ? '__all__' : cur));
    setLoading(false);
  }, [sb, day, meal, zoneIds, recipes]);

  useEffect(() => {
    void load();
  }, [load]);

  const printList = useCallback(async () => {
    if (!profile?.active_tenant_id) {
      setToast('Manca tenant attivo.');
      return;
    }
    setPrinting(true);
    try {
      const res = await openExternalFromAsync(async () => {
        const { data, error } = await sb.functions.invoke<{ pdf_url?: string }>('export-piatti-zona', {
          body: {
            tenant_id: profile.active_tenant_id,
            service_date: day,
            meal_service: meal,
            business_ids: zoneIds ?? undefined,
          },
        });
        if (error) throw error;
        if (!data?.pdf_url) throw new Error('Nessun PDF restituito');
        return data.pdf_url;
      });
      setToast(res.blocked ? 'Lista pronta — tocca "Apri PDF"' : 'Lista per destinazioni generata');
      if (res.blocked && res.url) window.open(res.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setToast(`Errore stampa: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setPrinting(false);
    }
  }, [sb, profile, day, meal, zoneIds]);

  const shown = activeDest === '__all__' ? summary : summary.filter((d) => d.destId === activeDest);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/today" />
          </IonButtons>
          <IonTitle>Riassunto per destinazione</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent style={{ '--background': '#eef2f7' } as React.CSSProperties}>
        <div style={{ padding: '12px 16px 96px' }}>
          <p style={{ fontSize: 13, color: '#475569', margin: '0 4px 10px' }}>
            Cosa va a ogni destinazione (per chi smista): piatti e porzioni per destinazione, con il dettaglio per reparto in
            cascata. Stesso contenuto della stampa <strong>Lista per destinazioni</strong>.
          </p>
          <IonList inset style={{ marginInline: 0 }}>
            <IonItem>
              <IonLabel position="stacked">Giorno</IonLabel>
              <IonInput type="date" value={day} onIonInput={(e) => setDay(String(e.detail.value ?? todayIso()))} />
            </IonItem>
          </IonList>
          <IonSegment value={meal} onIonChange={(e) => setMeal(String(e.detail.value ?? 'pranzo'))} style={{ margin: '4px 0 10px' }}>
            <IonSegmentButton value="pranzo">Pranzo</IonSegmentButton>
            <IonSegmentButton value="cena">Cena</IonSegmentButton>
          </IonSegment>

          {summary.length > 0 && (
            <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 8 }}>
              <IonChip color={activeDest === '__all__' ? 'primary' : 'medium'} onClick={() => setActiveDest('__all__')}>
                Tutte
              </IonChip>
              {summary.map((d) => (
                <IonChip
                  key={d.destId}
                  color={activeDest === d.destId ? 'primary' : 'medium'}
                  onClick={() => setActiveDest(d.destId)}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {d.destName}
                </IonChip>
              ))}
            </div>
          )}

          <IonButton
            expand="block"
            fill="outline"
            disabled={printing || summary.length === 0}
            onClick={() => {
              void printList();
            }}
            style={{ margin: '2px 0 10px' }}
          >
            {printing ? (
              <IonSpinner name="dots" />
            ) : (
              <>
                <IonIcon icon={printOutline} slot="start" />
                Stampa lista per destinazioni
              </>
            )}
          </IonButton>

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
              <IonSpinner />
            </div>
          ) : shown.length === 0 ? (
            <div style={{ background: '#fff', borderRadius: 14, padding: 16, color: '#64748b', fontSize: 13 }}>
              Nessun ordine per questo giorno/servizio.
            </div>
          ) : (
            shown.map((d) => {
              const wardsOpen = openWards[d.destId] ?? false;
              return (
                <div
                  key={d.destId}
                  style={{ background: '#fff', borderRadius: 14, padding: '12px 14px', marginBottom: 12, boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
                      {d.destName}
                      {d.city ? <span style={{ fontWeight: 400, color: '#64748b' }}> · {d.city}</span> : null}
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>
                      {d.totalPortions} porz.{d.bedCount > 0 ? ` · ${d.bedCount} letti` : ''}
                    </div>
                  </div>

                  {d.categories.map((c) => {
                    const col = CAT_COLOR[c.category] ?? { bg: '#f1f5f9', fg: '#475569' };
                    return (
                      <div key={c.category} style={{ marginTop: 10 }}>
                        <div
                          style={{
                            display: 'inline-block',
                            fontSize: 11,
                            fontWeight: 800,
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            background: col.bg,
                            color: col.fg,
                            borderRadius: 8,
                            padding: '3px 10px',
                            marginBottom: 6,
                          }}
                        >
                          {c.category}
                        </div>
                        {c.dishes.map((dish) => (
                          <div key={dish.label} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 14, padding: '2px 2px' }}>
                            <span style={{ color: '#334155', minWidth: 0, wordBreak: 'break-word' }}>{dish.name}</span>
                            <span style={{ color: '#0f172a', fontWeight: 700, whiteSpace: 'nowrap' }}>{dish.qty}</span>
                          </div>
                        ))}
                      </div>
                    );
                  })}

                  {d.wards.length > 0 && (
                    <div style={{ marginTop: 10, borderTop: '1px solid #f1f5f9', paddingTop: 8 }}>
                      <button
                        onClick={() => setOpenWards((s) => ({ ...s, [d.destId]: !wardsOpen }))}
                        style={{
                          background: 'none',
                          border: 'none',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          color: '#475569',
                          fontSize: 13,
                          fontWeight: 600,
                          padding: 0,
                          cursor: 'pointer',
                        }}
                      >
                        <IonIcon icon={wardsOpen ? chevronDown : chevronForward} />
                        Dettaglio reparti ({d.wards.length})
                      </button>
                      {wardsOpen &&
                        d.wards.map((w) => (
                          <div key={`${w.ward}-${w.service}`} style={{ marginTop: 8 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: '#334155', margin: '4px 0' }}>
                              {w.ward} · {MEAL_LABEL[w.service] ?? w.service} · {w.beds.length} {w.beds.length === 1 ? 'letto' : 'letti'}
                            </div>
                            {w.beds.map((b) => {
                              const courses = [b.primo, b.secondo, b.contorno, b.frutta].filter(Boolean);
                              return (
                                <div key={b.letto} style={{ fontSize: 12, color: '#475569', padding: '2px 0 2px 10px' }}>
                                  <strong>letto {b.letto}</strong>
                                  {b.diet ? ` — ${b.diet}` : ''}
                                  {courses.length > 0 && <span style={{ color: '#64748b' }}> · {courses.join(' / ')}</span>}
                                  {b.notes ? <span style={{ color: '#b45309' }}> · {b.notes}</span> : null}
                                </div>
                              );
                            })}
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
        <IonToast isOpen={toast !== null} message={toast ?? ''} duration={2500} onDidDismiss={() => setToast(null)} />
      </IonContent>
    </IonPage>
  );
}
